import { Avatar } from 'antd';
import { FC, Fragment, ReactNode, useContext, useState } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { MdOutlineMenu } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import SidebarDrawer from '../components/layouts/sidebar-drawer';
import { cn } from '../config/cn';
import { AuthContext } from '../context/auth.context.tsx';
import { SidebarContext } from '../context/sidebar.context.tsx';
import { useThemeStore } from '../store/theme';
import SidebarBottom from './sidebar-bottom';
import { useQuery } from '@tanstack/react-query';
import axios from '../config/axios';
import CopyableText from '../components/copyable-text';
import { Retailer } from '../types/retailers.types.ts';

type Props = {
	title: string;
	subtitle: string;
	children: ReactNode;
};

const AppLayout: FC<Props> = (props) => {
	const location = useLocation();
	const { isCollapsed, setIsCollapsed } = useContext(SidebarContext);
	const { user } = useContext(AuthContext);
	const { is_dark_mode } = useThemeStore();
	const { width } = useWindowSize();
	const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);

	// Fetch retailer profile if user is a retailer
	const { data: retailerProfile } = useQuery({
		queryKey: ['retailer-profile'],
		queryFn: async () => {
			const response = await axios.get('/retailers/profile', {
				withCredentials: true,
			});
			return response.data.results as Retailer;
		},
		enabled: !!user && user.role === 'retailer',
	});

	return (
		<Fragment>
			<div className="flex min-h-dvh">
				<div
					className={cn('sticky top-0 z-30 h-dvh bg-gray-50 transition-all dark:bg-dark-sidebar', {
						'w-24': isCollapsed,
						'w-72': !isCollapsed,
						hidden: width && width < 1100,
					})}
				>
					<div
						className={cn('relative flex h-24 items-center px-4 md:px-6', {
							'justify-center': isCollapsed,
						})}
					>
						{isCollapsed && user ? (
							<div className="flex items-center justify-center">
								{user.dp ? (
									<Avatar
										shape="circle"
										size="large"
										src={user.dp}
									/>
								) : (
									<Avatar>{user.first_name.charAt(0).toUpperCase()}</Avatar>
								)}
							</div>
						) : user ? (
							<div className="flex w-full items-center gap-2 rounded-md border px-3 py-2 dark:border-[#FFFFFF0D] dark:bg-[#1F1D2B]">
								<div>
									{user.dp ? (
										<Avatar
											shape="circle"
											size="large"
											src={user.dp}
										/>
									) : (
										<Avatar>{user.first_name.charAt(0).toUpperCase()}</Avatar>
									)}
								</div>

								<div>
									<p
										className="max-w-40 truncate text-base font-bold dark:text-white"
										title={`${user.first_name} ${user.last_name}`}
									>
										{user.first_name} {user.last_name}
									</p>
									<p className="max-w-40 truncate text-sm text-gray-400">
										{user.admin_type?.toUpperCase().replace(/_/g, ' ') || user.email}
									</p>
								</div>
							</div>
						) : null}

						{/*sidebar collapse*/}
						<div
							className="absolute -right-3 top-10 dark:-right-3.5"
							role={'button'}
							onClick={() => setIsCollapsed(!isCollapsed)}
						>
							<div className="flex items-center justify-center rounded-full border bg-white p-1.5 dark:border-primary dark:bg-primary dark:text-white">
								{isCollapsed ? <BsChevronRight size={13} /> : <BsChevronLeft size={13} />}
							</div>
						</div>
					</div>

					<div className="mx-5 -mt-px border-b dark:border-b-[#FFFFFF1A]" />

					<SidebarBottom />
				</div>
				<div className="relative flex-1 dark:bg-dark-bg">
					<div
						className={cn(
							'sticky top-0 z-20 flex h-20 items-center border-b bg-white min-[1100px]:h-24 dark:border-b-[#2d2b38] dark:bg-dark-bg',
							{
								'border-b-0': ['/dashboard'].includes(location.pathname),
							},
						)}
					>
						<div className="flex w-full items-center justify-between px-4 md:px-6">
							<div
								className="flex items-center gap-2 rounded-lg border p-2 min-[1100px]:hidden dark:border-[#2d2b38] dark:bg-[#1f1d2b] dark:text-white"
								onClick={() => {
									setSidebarDrawerOpen(true);
								}}
								role="button"
							>
								<MdOutlineMenu size={25} />
								<img
									src={`/images/logo.png`}
									alt={`menu-${is_dark_mode ? 'dark' : 'light'}`}
									className="ml-2 h-6"
								/>
							</div>
							<div className="hidden flex-col min-[1100px]:flex">
								<div className="w-fit text-2xl font-semibold text-black-7 dark:text-white">{props.title}</div>
								<div className="text-base font-medium text-gray-500 dark:text-gray-400">{props.subtitle}</div>
							</div>
							<div className="flex items-center gap-4">
								{/* Show retailer ID for retailers */}
								{user && user.role === 'retailer' && retailerProfile && (
									<div className="hidden md:block">
										<CopyableText text={retailerProfile.vendor_id}>
											<span className="mr-1 font-medium text-gray-600 dark:text-gray-300">
												ID: <span className="font-semibold text-primary">{retailerProfile.vendor_id}</span>
											</span>
										</CopyableText>
									</div>
								)}
							</div>
						</div>
					</div>

					{/*children*/}
					<div
						className={cn('px-4', {
							'pt-4': ['/dashboard'].includes(location.pathname) === false,
						})}
					>
						<div className="pb-5 min-[1100px]:hidden">
							<div className="w-fit text-2xl font-semibold text-black-7 dark:text-white">{props.title}</div>
							<div className="text-base font-medium text-gray-500 dark:text-gray-400">{props.subtitle}</div>
						</div>
						{props.children}
					</div>
				</div>
			</div>

			{/* sidebar drawer */}
			<SidebarDrawer
				open={sidebarDrawerOpen}
				close={() => setSidebarDrawerOpen(false)}
			/>
		</Fragment>
	);
};

export default AppLayout;
